import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  BookOpen, AlertTriangle, MessageSquare, Lightbulb, Ticket, LifeBuoy,
} from "lucide-react";
import AppNav from "../components/AppNav";
import LegalFooter from "../components/LegalFooter";
import { HelpCenter } from "../components/support/HelpCenter";
import { ReportProblemForm } from "../components/support/ReportProblemForm";
import { FeedbackForm } from "../components/support/FeedbackForm";
import { FeatureRequestForm } from "../components/support/FeatureRequestForm";
import { MyTickets } from "../components/support/MyTickets";
import { TicketThread } from "../components/support/TicketThread";

const TABS = [
  { key: "help", label: "Help Center", icon: BookOpen },
  { key: "report", label: "Report a Problem", icon: AlertTriangle },
  { key: "feedback", label: "Send Feedback", icon: MessageSquare },
  { key: "feature", label: "Feature Request", icon: Lightbulb },
  { key: "tickets", label: "My Tickets", icon: Ticket },
];

export default function SupportPage() {
  const [params, setParams] = useSearchParams();
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const requested = params.get("tab");
  const [tab, setTab] = useState(
    TABS.some((t) => t.key === requested) ? requested : (ticketId ? "tickets" : "help")
  );

  useEffect(() => {
    if (ticketId) setTab("tickets");
  }, [ticketId]);

  const pick = (key) => {
    setTab(key);
    if (ticketId) navigate("/support?tab=" + key);
    else setParams(key === "help" ? {} : { tab: key }, { replace: true });
  };

  const body = useMemo(() => {
    if (tab === "tickets") {
      if (ticketId) {
        return (
          <TicketThread
            ticketId={ticketId}
            onBack={() => navigate("/support?tab=tickets")}
          />
        );
      }
      return (
        <MyTickets
          onOpen={(id) => navigate(`/support/tickets/${id}`)}
          onReport={() => pick("report")}
        />
      );
    }
    if (tab === "report") {
      return <ReportProblemForm onOpenTicket={(id) => navigate(`/support/tickets/${id}`)} />;
    }
    if (tab === "feedback") return <FeedbackForm />;
    if (tab === "feature") return <FeatureRequestForm />;
    return <HelpCenter onContactSupport={() => pick("report")} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, ticketId, navigate]);

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <AppNav />
      <main className="pt-24 pb-16 px-4 md:px-8 max-w-5xl mx-auto relative z-10" data-testid="support-page">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            <LifeBuoy className="w-3 h-3" /> Help &amp; Support
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none">
            We read every word.
          </h1>
          <p className="text-base text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            Find an answer, report something broken, or tell us what to build
            next. Problems get a ticket you can follow; feedback and ideas
            don't need one.
          </p>
        </div>

        <div
          className="flex flex-wrap gap-2 mb-8 border-b border-zinc-800 pb-4"
          data-testid="support-tabs"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => pick(t.key)}
                data-testid={`support-tab-${t.key}`}
                aria-current={active ? "page" : undefined}
                className={`inline-flex items-center gap-2 text-[10px] md:text-[11px] font-mono uppercase tracking-widest px-3 md:px-4 py-2.5 border transition-colors duration-150 ${
                  active
                    ? "border-white bg-white text-zinc-950"
                    : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
                }`}
              >
                <Icon className="w-3 h-3" /> {t.label}
              </button>
            );
          })}
        </div>

        {body}

        <LegalFooter />
      </main>
    </div>
  );
}
